import Swal from 'sweetalert2'
import { playNotificationSound } from '@/composables/useNotificationSound'

let notifReg: ServiceWorkerRegistration | null = null

function waitForActivation(reg: ServiceWorkerRegistration): Promise<void> {
  if (reg.active) return Promise.resolve()
  const sw = reg.installing || reg.waiting
  if (!sw) return Promise.resolve()
  return new Promise(resolve => {
    sw.addEventListener('statechange', function handler() {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', handler)
        resolve()
      }
    })
  })
}

// Escopo dedicado (não '/') — em produção o service-worker.js do PWA
// (registerServiceWorker.ts, workbox com skipWaiting+clientsClaim) também
// registra em '/'. Dois scripts diferentes não podem ser o worker ativo do
// mesmo escopo ao mesmo tempo: o registro é único por escopo, então o
// segundo a ativar substitui o primeiro. Em produção isso fazia o
// service-worker.js do workbox tomar o lugar do sw-notif.js pouco depois do
// boot, apagando os listeners de 'notificationclick'/'push' — o botão
// "Continuar logado" do toast do Windows parava de responder, só em prod
// (no localhost o workbox nunca registra: registerServiceWorker.ts só roda
// com NODE_ENV === 'production'). clients.matchAll({includeUncontrolled:true})
// já cobre qualquer aba da origem independente do escopo, então isolar o
// escopo aqui não quebra achar a aba pra reenviar 'notif-continue-session'.
const NOTIF_SW_SCOPE = '/notif-sw-scope/'

export async function initNotificationSW(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Notif] serviceWorker não suportado')
    return
  }
  try {
    const reg = await navigator.serviceWorker.register('/sw-notif.js', { scope: NOTIF_SW_SCOPE })
    await waitForActivation(reg)
    notifReg = reg
  } catch (err) {
    console.warn('[Notif] falha ao registrar SW:', err)
  }
}

/**
 * Registro do sw-notif.js pronto pra uso (usado por pushSubscriptionService.ts
 * em vez de `navigator.serviceWorker.ready`) — como o escopo agora é dedicado
 * (ver NOTIF_SW_SCOPE acima), `ready` resolveria pro service-worker.js do PWA
 * (que controla a página em '/'), não pro worker que sabe tratar 'push'.
 */
export async function getNotifRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (notifReg) return notifReg
  await initNotificationSW()
  return notifReg
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.warn('[Notif] API Notification não disponível')
    return false
  }
  if (Notification.permission === 'granted') return true
  const perm = await Notification.requestPermission()
  return perm === 'granted'
}

// lib.dom.d.ts desta versão do TS ainda não tem `actions`/NotificationAction
// (parte da spec, mas não em todo lib — Chrome/Edge já suportam em runtime).
interface NotificationActionLike {
  action: string
  title: string
  icon?: string
}

interface PushNotificationExtra {
  /** Botões de ação no toast — só têm efeito quando exibidos via Service Worker (notifReg.active); ignorados no fallback `new Notification()`. */
  actions?: NotificationActionLike[]
  requireInteraction?: boolean
  data?: Record<string, unknown>
  /** URL do avatar pra substituir o ícone padrão do app (ex.: foto do contato numa notificação de mensagem). */
  icon?: string
  /** Epoch ms do evento original — o SO usa isso pra mostrar "há Xmin" em vez da hora em que a notificação chegou. */
  timestamp?: number
}

export async function showPushNotification(
  title: string,
  body: string,
  url: string,
  tag?: string,
  convId?: number,
  extra?: PushNotificationExtra,
): Promise<void> {
  if (!('Notification' in window)) { console.warn('[Notif] API não disponível'); return }
  if (Notification.permission !== 'granted') {
    console.warn('[Notif] permissão não concedida:', Notification.permission)
    return
  }

  playNotificationSound()

  const opts: NotificationOptions & { actions?: NotificationActionLike[]; timestamp?: number } = {
    body,
    icon: extra?.icon || '/img/icons/android-chrome-192x192.png',
    badge: '/img/icons/favicon-32x32.png',
    tag: tag || url,
    data: { url, convId, ...extra?.data },
    requireInteraction: extra?.requireInteraction ?? false,
    ...(extra?.actions ? { actions: extra.actions } : {}),
    ...(extra?.timestamp ? { timestamp: extra.timestamp } : {}),
  }

  // Tentativa 1: via SW (funciona mesmo com browser minimizado)
  if (notifReg?.active) {
    try {
      await notifReg.showNotification(title, opts)
      return
    } catch (e) {
      console.warn('[Notif] SW falhou, tentando direto:', e)
    }
  }

  // Tentativa 2: new Notification() direto
  try {
    const n = new Notification(title, opts)
    n.onclick = () => { window.open(url, '_blank'); n.close() }
  } catch (e) {
    console.error('[Notif] ambas tentativas falharam:', e)
  }
}

/**
 * Toast do aviso de inatividade — replica o popup em tela via Notification,
 * pra chegar como notificação do Windows mesmo com a aba minimizada/sem foco.
 * O botão "Continuar logado" é tratado em sw-notif.js (notificationclick),
 * que posta 'notif-continue-session' de volta pra aba (ver App.vue).
 */
export async function showAutoLogoutWarningNotification(secondsLeft: number): Promise<void> {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  await showPushNotification(
    'Você ainda está aí?',
    `Por inatividade, você será desconectado em ${secondsLeft}s.`,
    window.location.href,
    'auto-logout',
    undefined,
    {
      requireInteraction: true,
      data: { type: 'auto-logout' },
      actions: [{ action: 'continue-session', title: 'Continuar logado' }],
    },
  )
}

/** Dispara notificação de teste — use o botão no header para verificar se está funcionando */
export async function testNotification(): Promise<void> {
  console.log('[Notif] === TESTE DE NOTIFICAÇÃO ===')
  console.log('[Notif] Notification.permission:', Notification.permission)
  console.log('[Notif] notifReg:', notifReg)
  console.log('[Notif] notifReg.active:', notifReg?.active)

  const granted = await requestNotificationPermission()
  if (!granted) {
    console.error('[Notif] FALHA: permissão negada ou não concedida')
    Swal.fire({ title: 'Permissão negada', text: 'Permissão de notificação não concedida. Verifique as configurações do seu browser.', icon: 'warning' })
    return
  }

  await showPushNotification(
    'ISBET Wootdash — Teste ✓',
    'Notificações estão funcionando corretamente.',
    window.location.href,
    'notif-test',
  )
}
